import React, { useState, useMemo } from "react";
import { observer } from "mobx-react";
//icons
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
//mui
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
//styles
import styles from "../../styles/dashboard.module.css";
//loading
import loadingSvg from "../../../Assets/images/tube-spinner.svg";
//modal
import MyEmissionHotpotsModal from "../../modals/MyEmissionHotpotsModal";
import dashboardStore from "../../store/dashboardStore";
import { dashboardAPI } from "../../API/dashboardAPI";
//翻譯
import { useTranslation } from "react-i18next";
import { Divider } from "@mui/material";
import { formatNumber } from "../../utils/tools";

const ISO_LABELS = [
  { key: "1.1", label: "1.1 Stationary Combustion" },
  { key: "1.2", label: "1.2 Mobile Combustion" },
  { key: "1.3", label: "1.3 Industrial Processes" },
  { key: "1.4", label: "1.4 Fugitive Emissions" },
  {
    key: "1.5",
    label: "1.5 Land Use, Land Use Change and Forestry (LULUCF)",
  },
  { key: "2.1", label: "2.1 Imported Electricity" },
  { key: "2.2", label: "2.2 Imported Energy" },
  { key: "3.1", label: "3.1 Upstream Transport and Distribution for Goods" },
  {
    key: "3.2",
    label: "3.2 Downstream Transport and Distribution for Goods",
  },
  { key: "3.3", label: "3.3 Employee Commuting" },
  { key: "3.4", label: "3.4 Client and Visitor Transport" },
  { key: "3.5", label: "3.5 Business Travel" },
  { key: "4.1", label: "4.1 Goods Purchased by an Organization" },
  {
    key: "4.2",
    label:
      "4.2 Capital Goods that are Purchased and Amortized by an Organization",
  },
  {
    key: "4.3",
    label:
      "4.3 Disposal Services (of solid or liquid waste) Used by an Organization",
  },
  {
    key: "4.4",
    label: "4.4 Use of Assets (through leased equipment) by an Organization",
  },
  {
    key: "4.5",
    label:
      "4.5 Use of Services not Described in the above sub-categories by an Organization",
  },
  { key: "5.1", label: "5.1 Use Stage of the Product" },
  { key: "5.2", label: "5.2 Downstream Leased Assets" },
  { key: "5.3", label: "5.3 End of Life Stage of the Product" },
  { key: "5.4", label: "5.4 Investments" },
  {
    key: "6.1",
    label: "6.1 Indirect GHG Emissions from other sources",
  },
];

const GHG_LABELS = [
  { key: "1.1", label: "1.1 Direct Emissions from Stationary Combustion" },
  { key: "1.2", label: "1.2 Direct Emissions from Mobile Combustion" },
  { key: "1.3", label: "1.3 Direct Emissions from Process Sources" },
  { key: "1.4", label: "1.4 Direct Emissions from Fugitive Sources" },
  { key: "1.5", label: "1.5 Direct Emissions from Agricultural Sources" },
  {
    key: "2.1",
    label: "2.1 Indirect Emissions from Purchased/Acquired Electricity",
  },
  { key: "2.2", label: "2.2 Indirect Emissions from Purchased/Acquired Steam" },
  {
    key: "2.3",
    label: "2.3 Indirect Emissions from Purchased/Acquired Heating",
  },
  {
    key: "2.4",
    label: "2.4 Indirect Emissions from Purchased/Acquired Cooling",
  },
  { key: "3.1", label: "3.1 Purchased Goods and Services" },
  { key: "3.2", label: "3.2 Capital Goods" },
  {
    key: "3.3",
    label:
      "3.3 Fuel and Energy-related Activities(not included in Scope 1 or Scope 2)",
  },
  { key: "3.4", label: "3.4 Upstream Transportation and Distribution" },
  { key: "3.5", label: "3.5 Waste Generated in Operations" },
  { key: "3.6", label: "3.6 Business Travel" },
  { key: "3.7", label: "3.7 Employee Commuting" },
  { key: "3.8", label: "3.8 Upstream Leased Assets" },
  { key: "3.9", label: "3.9 Downstream Transportation and Distribution" },
  { key: "3.10", label: "3.10 Processing of Solid Products" },
  { key: "3.11", label: "3.11 Use of Solid Products" },
  { key: "3.12", label: "3.12 End-of-Life Treatment of Solid Products" },
  { key: "3.13", label: "3.13 Downstream Leased Assets" },
  { key: "3.14", label: "3.14 Franchise (Scope 1 and Scope 2)" },
  { key: "3.15", label: "3.15 Investments" },
];

const LinearProgressWithLabel = ({
  value,
  title,
  percentage,
  color,
  t,
  unitLabel,
}) => {
  return (
    <div className={styles.linearProgressWrapper}>
      {/* title */}
      <div className={styles.progressTitle}>{t(title)}</div>

      {/* lineBar */}
      <div className={styles.progressWithValueWrapper}>
        <div className={styles.progressBar}>
          <LinearProgress
            variant="determinate"
            value={parseFloat(percentage)}
            sx={{
              height: 30,
              borderRadius: 5,
              backgroundColor: "#ccc",
              "& .MuiLinearProgress-bar": {
                backgroundColor: color || "#4caf50", // 預設顏色
              },
            }}
          />
          <div className={styles.progressPercentageOnBar}>{percentage}</div>
        </div>

        {/* value */}
        <div className={styles.valueUnitWrapper}>
          <div className={styles.progressValue}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {Number(value).toLocaleString()}
            </Typography>
          </div>
          <div className={styles.progressUnit}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {unitLabel}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

const MyEmissionHotpots = observer(() => {
  const { t } = useTranslation("ActivityMain");
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const unitLabel =
    dashboardStore.unit === "20"
      ? "kgCO2e"
      : dashboardStore.unit === "21"
        ? "tCO2e"
        : "";

  const xLabels = dashboardStore.standardId === 1 ? ISO_LABELS : GHG_LABELS;

  const currentColors = useMemo(() => {
    return xLabels.map((item) => {
      // 修改點：因為 xLabels 現在是物件陣列，所以要取 item.key
      const keyStr = item.key;

      const scopeIndex = parseInt(keyStr.split(".")[0]) - 1; // "1.1" -> 0, "2.1" -> 1
      // 防止索引超出 themeColor 範圍
      const safeIndex = Math.min(
        scopeIndex,
        dashboardStore.themeColor.length - 1,
      );
      return dashboardStore.themeColor[safeIndex];
    });
  }, [xLabels, dashboardStore.themeColor]);

  //顏色選用
  // const colorArray = dashboardStore.groupBy === "scope" ? colorSet : colorSet6;

  const topThree =
    dashboardStore.barChartData &&
    Object.entries(dashboardStore.barChartData)
      .map(([key, data]) => ({ key, ...data }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 3)
      .map((item) => {
        const labelKey = item.key.replace("_", "."); // e.g. "1_2" → "1.2"
        const match = xLabels.find((x) => x.key === labelKey);
        const index = xLabels.findIndex((x) => x.key === labelKey);

        return {
          title: match?.label || labelKey, // 找不到 fallback 使用 key
          value: item.value,
          percentage: `${item.percentage}%`,
          color: currentColors[index] || "#ccc", // 找不到就給預設顏色
        };
      });

  //判斷Value是不是都是0
  const isAllZero = (data) => {
    return data?.every((item) => item.value === 0);
  };

  return (
    <div className={`${styles.chartContent} ${styles.grid_4}`}>
      {/* 標題列 */}
      <div className={`${styles.chartTitle} ${styles.myEmissionHotspotsTitle}`}>
        {/* {t("My Emission Hotspots (Top 5)")} */}
        {t("My Emission Hotspots (Top 3)")}
        {/* 如果沒資料不顯示彈跳出視窗按鈕 */}
        {!isAllZero(topThree) && (
          <OpenInNewIcon sx={{ cursor: "pointer" }} onClick={handleOpenModal} />
        )}
      </div>

      {/* 主體內容 */}
      {/* <div className={styles.chartBody}> */}
      <div className={styles.chart}>
        {/* 載入中畫面 */}
        {dashboardStore.barChartDataLoading ? (
          <div className={styles.loading}>
            <img
              className={styles.loadingImg}
              src={loadingSvg}
              alt="loadingSvg"
            />
          </div>
        ) : //無資料
        isAllZero(topThree) ? (
          <div className={styles.noData}>{t("No data")}!</div>
        ) : (
          // 進度條區塊
          <div className={styles.linearProgressArea}>
            {topThree.map((item, index) => (
              <React.Fragment key={item.title || index}>
                <LinearProgressWithLabel
                  key={index}
                  value={item.value}
                  title={item.title}
                  percentage={item.percentage}
                  color={item.color}
                  t={t}
                  unitLabel={unitLabel}
                />
                {index < topThree.length - 1 && <Divider variant="middle" />}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      {/* 彈出視窗 */}
      <MyEmissionHotpotsModal open={isOpen} setIsOpen={setIsOpen} />
    </div>
  );
});

export default MyEmissionHotpots;
